// =============================================================================
// 📁 File: src/utils/axiosInstance.ts
// =============================================================================
// CTO Recovery Prompt — Canonical Axios Client (v2026.02)
//
// Purpose:
// - Single HTTP surface for all frontend → backend calls
// - Enforces canonical headers:
//     Authorization: Bearer <access_token>
//     X-Tenant-ID: <tenant_id>   (ONLY when required)
// - Implements safe refresh-on-401 with SINGLE retry per request
// - NEVER refreshes immediately after login (only reacts to 401)
// - NEVER hard-redirects to "/login" (must be tenant-safe)
// - Avoids infinite loops and handles concurrent 401 storms correctly
//
// Invariants (LOCKED):
// 1) baseURL must end with "/api/v1" exactly once.
// 2) Portfolio endpoints (/portfolio/*) MUST NOT require X-Tenant-ID.
//    (They may still receive it, but they MUST work without it.)
// 3) On 401:
//    - Try refresh once (if refresh_token exists)
//    - If refresh succeeds → retry original request once
//    - If refresh fails → clearSession() and tenant-safe redirect to "/:tenantSlug/login"
// 4) Do NOT mutate auth state at import time.
// 5) Do NOT swallow errors; return rejected promises for callers to handle.
// =============================================================================

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { refreshToken, clearSession, getTenantSlugFromStorage } from "@/utils/auth";

// ----------------------------------------------------------------------------
// Canonical baseURL normalization
// ----------------------------------------------------------------------------
// VITE_API_URL can be:
// - http://127.0.0.1:8000
// - http://127.0.0.1:8000/api/v1
// This guarantees ".../api/v1" exactly once.
const rawBase = String(import.meta.env.VITE_API_URL || "").replace(/\/+$/, "");
const baseURL = rawBase.endsWith("/api/v1") ? rawBase : `${rawBase}/api/v1`;

// ----------------------------------------------------------------------------
// Instance
// ----------------------------------------------------------------------------
const axiosInstance: AxiosInstance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ----------------------------------------------------------------------------
// Request interceptor — attach Authorization + X-Tenant-ID (tenant-scoped only)
// ----------------------------------------------------------------------------
axiosInstance.interceptors.request.use((config) => {
  const accessToken = sessionStorage.getItem("access_token");
  const tenantId = sessionStorage.getItem("tenant_id");

  // Always attach Authorization if present
  if (accessToken) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // Attach X-Tenant-ID only when we actually have it.
  // IMPORTANT: Portfolio endpoints must not depend on it, but sending it is harmless.
  if (tenantId) {
    config.headers = config.headers ?? {};
    config.headers["X-Tenant-ID"] = tenantId;
  }

  return config;
});

// ----------------------------------------------------------------------------
// Response interceptor — safe refresh on 401 with concurrency control
// ----------------------------------------------------------------------------
//
// Why this structure:
// - Multiple API calls can 401 at the same time when token expires.
// - We must refresh ONCE and replay all failed requests after refresh.
// - Avoid infinite loops: never try to refresh if the 401 came from refresh endpoint.
// - Never do window.location.href="/login" (tenant loss). Redirect is tenant-safe.
//
type RetryableRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
};

let refreshPromise: Promise<boolean> | null = null;

function isAuthRefreshRequest(config?: AxiosRequestConfig): boolean {
  const url = String(config?.url || "");
  // axiosInstance baseURL already includes /api/v1, but url here is the path part.
  return url.includes("/auth/refresh-token");
}

function isInteractiveAuthRequest(config?: AxiosRequestConfig): boolean {
  const url = String(config?.url || "");
  return (
    url.includes("/auth/login") ||
    url.includes("/auth/forgot-password/request") ||
    url.includes("/auth/forgot-password/reset") ||
    url.includes("/auth/public/")
  );
}

function tenantSafeRedirectToLogin() {
  const slug = getTenantSlugFromStorage(); // already returns a safe fallback
  window.location.assign(`/${slug}/login`);
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const originalRequest = (error.config || {}) as RetryableRequestConfig;

    // Not a 401 → propagate
    if (status !== 401) {
      return Promise.reject(error);
    }

    // Expected auth-screen validation failures must stay on the current screen.
    if (isInteractiveAuthRequest(originalRequest)) {
      return Promise.reject(error);
    }

    // If refresh endpoint itself 401s → do NOT loop
    if (isAuthRefreshRequest(originalRequest)) {
      clearSession();
      tenantSafeRedirectToLogin();
      return Promise.reject(error);
    }

    // If already retried once → propagate (prevents infinite loops)
    if (originalRequest._retry) {
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    // No refresh_token available → force re-login
    const refreshTokenValue = sessionStorage.getItem("refresh_token");
    if (!refreshTokenValue) {
      clearSession();
      tenantSafeRedirectToLogin();
      return Promise.reject(error);
    }

    // Concurrency control: create one refreshPromise and share it
    if (!refreshPromise) {
      refreshPromise = (async () => {
        try {
          const ok = await refreshToken();
          return ok;
        } finally {
          // Always reset so future expiries can refresh again
          refreshPromise = null;
        }
      })();
    }

    const refreshed = await refreshPromise;

    if (!refreshed) {
      clearSession();
      tenantSafeRedirectToLogin();
      return Promise.reject(error);
    }

    // Refresh succeeded → retry original request with new access token
    const newAccessToken = sessionStorage.getItem("access_token");
    if (newAccessToken) {
      originalRequest.headers = originalRequest.headers ?? {};
      (originalRequest.headers as any).Authorization = `Bearer ${newAccessToken}`;
    }

    return axiosInstance(originalRequest);
  }
);

export default axiosInstance;
